import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ColumnDef, Row, SortingState, Updater } from "@tanstack/react-table";
import DataTable from "@/components/controls/DataTable/DataTable";
import {
  GroupRankResolvers,
  statusRank
} from "@/entities/dataTables/groupOrdering";
import NewQuickView from "@/components/layouts/NewQuickView/NewQuickView";
import { useNewQuickViewDrawer } from "@/services/hooks/entities/useNewQuickViewDrawer";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import {
  LeadColumn,
  LeadData,
  LeadStatus,
  SalesExplorationStatus
} from "@/pages/leads/types";
import { getEnumTKey } from "@/utils/strings";
import {
  buildGroupSummaries,
  rekeyGroupSummaries
} from "@/components/controls/DataTable/groupSummaries";
import { leadGroupAggregationField } from "@/pages/leads/groupSummaries";
import { useCompanies } from "@/pages/companies/hooks";
import { GenericAggregations } from "@/services/api/types";
import { tWithFallback } from "@/utils/i18n";
import { useUsers } from "@/entities/users/hooks";
import { createDataMapById } from "@/utils/datamaps";
import Text from "@/components/ui/text";
import { StatusBadge } from "@/components/ui/status-badge";
import TableNameCell from "@/components/controls/TableNameCell";
import { getFullName } from "@/entities/people/helpers";
import { PersonData } from "@/entities/people/types";
import {
  displayNumberOrFallback,
  formatNumberOrFallback
} from "@/utils/numbers";
import { getCurrentLocale } from "@/utils/locale";
import { simpleDate, toCalendarDate } from "@/utils/datetime";
import { createEnumSortingFn } from "@/utils/enums";
import { ColumnData } from "@/entities/dataTables/types";
import { APIFieldItem } from "@/pages/settings/types";
import { Filters } from "@/services/api/types";
import { ViewDataConfiguration } from "@/entities/views/types";
import { DataTableFilter } from "@/components/controls/DataTableFilters/DataTableFilters";
import {
  mapSelectedCustomColumnsToTableColumns,
  getTempView
} from "@/entities/dataTables/helpers";
import { useFieldVisibility } from "@/pages/settings/hooks";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { useLicenseServiceItems } from "@/entities/settings/hooks";
import { useLeadTypes } from "@/pages/leads/hooks";
import {
  leadStatusColors,
  salesExplorationStatusColors,
  sourcesFromIdx
} from "@/pages/leads/helpers";

const HARDCODED_LEAD_COLUMN_IDS = new Set<string>(Object.values(LeadColumn));

interface Props {
  data: LeadData[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  onLoadMore: () => void;
  filters: Filters;
  setFilters?: (filters: Filters | ((prev: Filters) => Filters)) => void;
  filterOptions?: DataTableFilter[];
  sorting: SortingState;
  onSortingChange: (updater: Updater<SortingState>) => void;
  groupBy?: string[];
  /** Grouped by the same field as the table whenever the API can group on it. */
  aggregations?: GenericAggregations<string> | object;
  isAggregationsLoading?: boolean;
  columnsState?: ColumnData[];
  onColumnsChange?: (updatedColumns?: ColumnData[]) => void;
  customFields?: APIFieldItem[];
  selectedView: ViewDataConfiguration;
  tableId: string;
  viewReady?: boolean;
  hasActiveFilters?: boolean;
  onClearFilters?: () => void;
  onClearSearch?: () => void;
  isError?: boolean;
  onRetry?: () => void;
  customEmptyState?: React.ReactNode;
  className?: string;
}

// Status groups follow the order its enum declares, not whichever column is sorted.
const GROUP_RANK_RESOLVERS: GroupRankResolvers<LeadData> = {
  [LeadColumn.STATUS]: statusRank
};

const supportsLeadGroupSummaries = (groupField?: string) =>
  !!leadGroupAggregationField(groupField);

const LeadsTable: React.FC<Props> = ({
  data,
  isLoading,
  isFetchingNextPage,
  hasNextPage,
  onLoadMore,
  filters,
  setFilters,
  filterOptions,
  sorting,
  onSortingChange,
  groupBy = [],
  aggregations,
  isAggregationsLoading = false,
  columnsState = [],
  onColumnsChange,
  customFields,
  selectedView,
  tableId,
  viewReady = true,
  hasActiveFilters = false,
  onClearFilters,
  onClearSearch,
  isError = false,
  onRetry,
  customEmptyState,
  className = "h-full"
}) => {
  const { t } = useTranslation();
  const {
    drawerState: newDrawerState,
    openDrawer: newOpenDrawer,
    closeDrawer: newCloseDrawer
  } = useNewQuickViewDrawer();

  const { isFieldVisible, isLoaded: fieldsLoaded } = useFieldVisibility(
    EntityType.LEAD
  );
  const { enabled: hideLicenseFields } = useFeatureFlag(
    FeatureFlags.CRM_7447_HIDE_LICENSE_FIELDS
  );

  const { licenseItems: licenseTypes } = useLicenseServiceItems();
  const leadTypes = useLeadTypes().data?.leadsTypes;
  const sources = useMemo(() => leadTypes ?? [], [leadTypes]);

  const groupField = groupBy[0];
  const isOwnerGrouping =
    groupField === LeadColumn.SDR_OWNER || groupField === LeadColumn.AE_OWNER;
  const grouped = aggregations as GenericAggregations<string> | undefined;

  const ownerIds = useMemo(() => {
    const ids = new Set<string>();
    data.forEach((lead) => {
      if (lead.sdrOwner) ids.add(lead.sdrOwner);
      if (lead.aeOwner) ids.add(lead.aeOwner);
      const tokenHolder = (lead as LeadData & { tokenHolder?: string | null })
        .tokenHolder;
      if (tokenHolder) ids.add(tokenHolder);
    });
    // Owners of groups nothing has scrolled to yet still need a name.
    if (isOwnerGrouping) {
      grouped?.data?.forEach((group) => {
        if (group.fieldValue) ids.add(group.fieldValue);
      });
    }
    return Array.from(ids);
  }, [data, grouped, isOwnerGrouping]);
  const { users: ownerUsers } = useUsers(ownerIds);

  // Grouping by company names a group per company in the whole filtered set, so
  // the names come from the summaries' ids as well as from the loaded rows.
  const isCompanyGrouping = groupField === LeadColumn.COMPANY_NAME;
  const groupCompanyIds = useMemo(
    () =>
      isCompanyGrouping
        ? (grouped?.data ?? [])
            .map((group) => group.fieldValue)
            .filter((companyId) => !!companyId)
        : [],
    [isCompanyGrouping, grouped]
  );
  const { entities: groupCompanies } = useCompanies(groupCompanyIds, {
    enabled: groupCompanyIds.length > 0
  });
  const companyNameById = useMemo(() => {
    const names: Record<string, string> = {};
    data.forEach((lead) => {
      if (lead.companyId && lead.companyName)
        names[lead.companyId] = lead.companyName;
    });
    groupCompanies.forEach((company) => {
      if (company?.id && company.name) names[company.id] = company.name;
    });
    return names;
  }, [data, groupCompanies]);
  const usersDataMap = useMemo(
    () => createDataMapById(ownerUsers),
    [ownerUsers]
  );
  const licenseTypesDataMap = useMemo(
    () => createDataMapById(licenseTypes),
    [licenseTypes]
  );

  // The API counts and averages every group over the whole filtered set, so a
  // group header reads the same on the first page as on the last. The keys are
  // the raw field values the column groups on, so only company grouping - where
  // the column reads the name and the server grouped on the id - is re-keyed.
  const groupSummaries = useMemo(() => {
    if (!supportsLeadGroupSummaries(groupField)) return undefined;
    const byFieldValue = buildGroupSummaries(
      grouped,
      [LeadColumn.OPEN_FOR_DAYS],
      isAggregationsLoading
    );
    if (isCompanyGrouping) {
      // The server grouped on the company id; the column reads the name. An id
      // with no name yet keeps its own key, matching no group rather than
      // folding into the wrong one.
      return rekeyGroupSummaries(
        byFieldValue,
        (companyId) => companyNameById[companyId] ?? companyId
      );
    }
    return byFieldValue;
  }, [
    companyNameById,
    groupField,
    grouped,
    isAggregationsLoading,
    isCompanyGrouping
  ]);

  const knownGroups = useMemo(() => {
    if (!groupSummaries) return undefined;
    if (isCompanyGrouping) {
      // A key still holding an unresolved id names no group on screen yet.
      const resolvedNames = new Set(Object.values(companyNameById));
      return Object.keys(groupSummaries)
        .filter((value) => value === "" || resolvedNames.has(value))
        .sort((a, b) => a.localeCompare(b))
        .map((value) => ({
          value,
          label: value || t("tables.notAvailable")
        }));
    }
    return Object.keys(groupSummaries).map((value) => {
      if (groupField === LeadColumn.STATUS) {
        const status = Number(value) as LeadStatus;
        return {
          value,
          label: tWithFallback(
            getEnumTKey("leads", "statusValues", LeadStatus, status),
            value
          )
        };
      }
      if (isOwnerGrouping) {
        return {
          value,
          label: usersDataMap[value]?.displayName || t("notFound")
        };
      }
      return {
        value,
        label: sourcesFromIdx(t, sources, Number(value)) || t("unknown")
      };
    });
  }, [
    groupSummaries,
    groupField,
    isOwnerGrouping,
    isCompanyGrouping,
    companyNameById,
    usersDataMap,
    sources,
    t
  ]);

  const onRowClick = useCallback(
    (lead: LeadData) => {
      newOpenDrawer(lead.id, EntityType.LEAD);
    },
    [newOpenDrawer]
  );

  const selectedColumns = useMemo(() => {
    if (columnsState.length > 0) {
      return columnsState;
    }
    const tempView = getTempView(selectedView);
    return tempView?.config?.columns || selectedView?.config?.columns || [];
  }, [columnsState, selectedView]);

  const columns = useMemo(() => {
    const columnDefs: ColumnDef<LeadData, any>[] = [
      {
        id: LeadColumn.NAME,
        accessorKey: LeadColumn.NAME,
        header: t(`leads.${LeadColumn.NAME}`),
        cell: (info) => (
          <TableNameCell info={info} entityType={EntityType.LEAD} />
        )
      },
      {
        id: LeadColumn.COMPANY_NAME,
        accessorKey: LeadColumn.COMPANY_NAME,
        header: t(`leads.${LeadColumn.COMPANY_NAME}`),
        cell: (info) => info.getValue() || t("notFound"),
        meta: { filterKey: "companyId" }
      },
      {
        id: LeadColumn.PRODUCT_NAME,
        accessorKey: LeadColumn.PRODUCT_NAME,
        header: t(`leads.${LeadColumn.PRODUCT_NAME}`),
        cell: (info) => info.getValue() || t("tables.notAvailable")
      },
      {
        id: LeadColumn.TOKEN_HOLDER,
        accessorKey: LeadColumn.TOKEN_HOLDER,
        header: t(`leads.${LeadColumn.TOKEN_HOLDER}`),
        cell: (info) =>
          usersDataMap[info.getValue() as string]?.displayName || t("notFound")
      },
      {
        id: LeadColumn.SDR_OWNER,
        accessorKey: LeadColumn.SDR_OWNER,
        header: t(`leads.${LeadColumn.SDR_OWNER}`),
        cell: (info) =>
          usersDataMap[info.getValue() as string]?.displayName || t("notFound")
      },
      {
        id: LeadColumn.AE_OWNER,
        accessorKey: LeadColumn.AE_OWNER,
        header: t(`leads.${LeadColumn.AE_OWNER}`),
        cell: (info) =>
          usersDataMap[info.getValue() as string]?.displayName || t("notFound")
      },
      {
        id: LeadColumn.STATUS,
        accessorKey: LeadColumn.STATUS,
        meta: { filterKey: LeadColumn.STATUS },
        header: t(`leads.${LeadColumn.STATUS}`),
        sortingFn: createEnumSortingFn(LeadStatus),
        minSize: 140,
        cell: (info) => {
          const statusKey = info.row.original.status;
          const valueKey = getEnumTKey(
            "leads",
            "statusValues",
            LeadStatus,
            statusKey
          );
          return (
            <StatusBadge statusColor={leadStatusColors[statusKey]}>
              {tWithFallback(valueKey, info.row.original.statusResolved)}
            </StatusBadge>
          );
        }
      },
      {
        id: LeadColumn.OPEN_FOR_DAYS,
        accessorKey: LeadColumn.OPEN_FOR_DAYS,
        header: t(`leads.${LeadColumn.OPEN_FOR_DAYS}`),
        sortingFn: "basic",
        cell: (info) =>
          displayNumberOrFallback(
            info.getValue() as number | null | undefined,
            ""
          )
      },
      {
        id: LeadColumn.LICENSE_ID,
        accessorKey: LeadColumn.LICENSE_ID,
        header: t(`leads.${LeadColumn.LICENSE_ID}`),
        cell: (info) => {
          const value = licenseTypesDataMap[info.getValue()]?.name;
          return value || t("unknown");
        }
      },
      {
        id: LeadColumn.NUMBER_OF_LICENSES,
        accessorKey: LeadColumn.NUMBER_OF_LICENSES,
        sortingFn: "basic",
        header: t(`leads.${LeadColumn.NUMBER_OF_LICENSES}`),
        cell: (info) =>
          formatNumberOrFallback(
            info.getValue() as number | null | undefined,
            t("tables.notAvailable"),
            0,
            false,
            false,
            true,
            getCurrentLocale(),
            true
          )
      },
      {
        id: LeadColumn.SOURCE,
        accessorKey: LeadColumn.SOURCE,
        header: t(`leads.source`),
        cell: (info) => {
          const value = sourcesFromIdx(t, sources, info.getValue());
          return value || t("unknown");
        }
      },
      {
        id: LeadColumn.SALES_EXPLORATION_MEETING_DATE,
        accessorKey: LeadColumn.SALES_EXPLORATION_MEETING_DATE,
        header: t(`leads.${LeadColumn.SALES_EXPLORATION_MEETING_DATE}`),
        sortingFn: "datetime",
        meta: { groupByMonth: true, localDay: true },
        cell: (info) =>
          info.getValue()
            ? simpleDate(toCalendarDate(info.getValue() as string), "short")
            : t("tables.notAvailable")
      },
      {
        id: LeadColumn.SALES_EXPLORATION_STATUS,
        accessorKey: "salesExplorationStatusDisplay",
        header: t(`leads.${LeadColumn.SALES_EXPLORATION_STATUS}`),
        cell: (info) => {
          const status = info.row.original.salesExplorationStatus;
          if (!status) return t("tables.notAvailable");
          const valueKey = getEnumTKey(
            "leads",
            "salesExplorationStatusValues",
            SalesExplorationStatus,
            status
          );
          return (
            <StatusBadge statusColor={salesExplorationStatusColors[status]}>
              {tWithFallback(
                valueKey,
                info.row.original.salesExplorationStatusDisplay
              )}
            </StatusBadge>
          );
        }
      },
      {
        id: LeadColumn.CONTACT,
        // The row carries the contact as an id plus its name parts; grouping keys off the id.
        accessorFn: (lead) => lead.contactId,
        header: t(`leads.${LeadColumn.CONTACT}`),
        cell: (info) => {
          const lead = info.row.original as LeadData;
          if (lead.contactFirstName || lead.contactLastName) {
            return getFullName({
              firstName: lead.contactFirstName || "",
              lastName: lead.contactLastName || ""
            } as PersonData);
          }
          return t("unknown");
        }
      },
      {
        id: LeadColumn.CONTACT_JOB_TITLE,
        accessorKey: LeadColumn.CONTACT_JOB_TITLE,
        header: t(`leads.${LeadColumn.CONTACT_JOB_TITLE}`),
        cell: (info) => info.getValue() || t("unknown")
      },
      {
        id: LeadColumn.CONTACT_EMAIL,
        accessorKey: LeadColumn.CONTACT_EMAIL,
        header: t(`leads.${LeadColumn.CONTACT_EMAIL}`),
        cell: (info) => {
          const email = info.getValue() as string;
          return email ? (
            <Text
              variant="body"
              copyable
              copyLabels={{ tooltip: t("copyTooltip"), copied: t("copied") }}
            >
              {email}
            </Text>
          ) : (
            t("unknown")
          );
        }
      },
      {
        header: t(`tables.${LeadColumn.CREATED_AT}`),
        accessorKey: LeadColumn.CREATED_AT,
        sortingFn: "datetime",
        meta: { groupByMonth: true, localDay: true },
        cell: (info) =>
          info.getValue()
            ? simpleDate(toCalendarDate(info.getValue() as string), "short")
            : t("tables.notAvailable")
      },
      {
        header: t(`leads.${LeadColumn.CLOSED_AT}`),
        accessorKey: LeadColumn.CLOSED_AT,
        sortingFn: "datetime",
        meta: { groupByMonth: true, localDay: true },
        cell: (info) =>
          info.getValue()
            ? simpleDate(toCalendarDate(info.getValue() as string), "short")
            : t("tables.notAvailable")
      },
      ...mapSelectedCustomColumnsToTableColumns(
        selectedColumns,
        customFields,
        t,
        HARDCODED_LEAD_COLUMN_IDS,
        groupBy
      )
    ];
    return columnDefs.filter((col) => {
      const key = (col.id ??
        (col as { accessorKey?: string }).accessorKey) as string;
      if (
        hideLicenseFields &&
        (key === LeadColumn.LICENSE_ID || key === LeadColumn.NUMBER_OF_LICENSES)
      ) {
        return false;
      }
      return !fieldsLoaded || isFieldVisible(key);
    });
  }, [
    usersDataMap,
    licenseTypesDataMap,
    sources,
    selectedColumns,
    customFields,
    isFieldVisible,
    fieldsLoaded,
    hideLicenseFields,
    t,
    groupBy
  ]);

  return (
    <>
      <DataTable
        groupRankResolvers={GROUP_RANK_RESOLVERS}
        key={tableId}
        dataTest={tableId}
        columns={columns}
        initialSortBy={sorting}
        manualSorting
        onSortingChange={onSortingChange}
        data={data}
        headerClassName="border-none"
        stickyLeftColumn
        resizableColumns
        className={className}
        isInfiniteScroll
        isLoadingMore={isFetchingNextPage}
        isDataLoading={isLoading || !viewReady}
        hasMore={hasNextPage}
        onLoadMore={onLoadMore}
        loadingThreshold={100}
        groupBy={groupBy}
        groupingConfig={{
          typeLabel: t("leads.bottomKPI.total"),
          groups: knownGroups,
          groupSummaries,
          subtotalAggregations: [
            {
              key: "avgOpenForDays",
              label: t("leads.bottomKPI.avgOpenForDays"),
              aggregate: (rows) => {
                const openForDaysValues = rows
                  .map((row) => row.openForDays)
                  .filter((days) => typeof days === "number");
                if (openForDaysValues.length === 0) return 0;
                return (
                  openForDaysValues.reduce((acc, days) => acc + days, 0) /
                  openForDaysValues.length
                );
              },
              fromSums: (sums, _count, counts) =>
                counts[LeadColumn.OPEN_FOR_DAYS]
                  ? sums[LeadColumn.OPEN_FOR_DAYS] /
                    counts[LeadColumn.OPEN_FOR_DAYS]
                  : 0,
              format: (value) => `${Math.round(value)}`
            }
          ]
        }}
        enableColumnReorder
        tableId={tableId}
        selectedView={selectedView}
        columnsState={columnsState}
        onColumnsChange={onColumnsChange}
        onRowClick={(row: Row<any>) => onRowClick(row.original)}
        filterOptions={filterOptions}
        filters={filters}
        setFilters={setFilters}
        hasActiveFilters={hasActiveFilters}
        onClearFilters={onClearFilters}
        onClearSearch={onClearSearch}
        isError={isError}
        onRetry={onRetry}
        customEmptyState={customEmptyState}
      />
      <NewQuickView
        isOpen={newDrawerState.isOpen}
        entityId={newDrawerState.entityId}
        entityType={EntityType.LEAD}
        onClose={newCloseDrawer}
      />
    </>
  );
};

export default LeadsTable;
